import { useState } from 'react'
import { useNavigate } from 'react-router-dom'

import AuthorizeUser from 'modules/core/components/AuthorizeUser'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { checkFeatures } from 'modules/core/hooks/useFeatures'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { NewBudgetWizard } from 'modules/finance/components/NewBudgetWizard'
import { SelectBudgetTypeModal } from 'modules/finance/components/SelectBudgetTypeModal'
import { TrainingsBudgetWizard } from 'modules/finance/components/TrainingsBudgetWizard'
import { useBudgetsPage } from 'modules/finance/hooks/useBudgets'

type BudgetType = 'selection' | 'expenses' | 'trainings'

const NewBudgetPage = () => {
  const navigate = useNavigate()
  const { navigation } = useNavigation()

  const {
    company: { id: companyId },
  } = useCurrentSession()

  const { legalEntities, features } = useBudgetsPage(companyId)
  const { has_dev_training_budgets } = checkFeatures(['dev_training_budgets'], features)

  // When feature flag is enabled, start with selection modal
  // Otherwise go directly to expenses wizard
  const [currentView, setCurrentView] = useState<BudgetType>(
    has_dev_training_budgets ? 'selection' : 'expenses'
  )

  const handleSuccess = (budgetId: string) => {
    app.setFlash({ type: 'correct', message: i18n.t('common.saved_successfully') })
    navigate(navigation.expenses.budgets.detail.fullPath({ budgetId }))
  }

  const handleError = () => {
    app.setFlash({ type: 'wrong', message: i18n.t('errors.generic_error') })
  }

  const handleClose = () => {
    navigate(navigation.expenses.budgets.fullPath())
  }

  const handleSelectTrainings = () => {
    setCurrentView('trainings')
  }

  const handleSelectExpenses = () => {
    setCurrentView('expenses')
  }

  return (
    <AuthorizeUser authorizeFn={() => true}>
      {currentView === 'selection' && (
        <SelectBudgetTypeModal
          onClose={handleClose}
          onSelectTrainings={handleSelectTrainings}
          onSelectExpenses={handleSelectExpenses}
        />
      )}

      {currentView === 'expenses' && (
        <NewBudgetWizard
          legalEntities={legalEntities}
          onSuccess={handleSuccess}
          onError={handleError}
        />
      )}

      {currentView === 'trainings' && (
        <TrainingsBudgetWizard
          legalEntities={legalEntities}
          onSuccess={handleSuccess}
          onError={handleError}
        />
      )}
    </AuthorizeUser>
  )
}

export default NewBudgetPage
